import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogType,
    Label,
    PrimaryButton,
    ProgressIndicator,
    Stack,
    StackItem,
    Text,
    VerticalDivider,
} from "@fluentui/react";
import React, {CSSProperties, FormEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {TFunction, useTranslation} from "react-i18next";
import _ from "lodash";
import {useBoolean} from "@fluentui/react-hooks";
import {slugify} from "transliteration";
import {TenantResource} from "../enums/TenantResource";
import {TBeforeSubmitFormCallback, useTenantResourceSingle,} from "../hooks/useTenantResourceSingle";
import {hr} from "../locales/hr";
import {CustomSearchBox} from "./CustomSearchBox";
import {LocalDatePicker} from "./LocalDatePicker";
import {GetApiError, ShowApiError} from "./ShowApiError";
import {MultilineTextComponent} from "../components/MultilineTextComponent";
import {
    BlobElement,
    ButtonElement,
    CheckboxElement,
    CMEditorElement,
    CustomRenderElement,
    CustomSearchBoxElement,
    DateAndTimeElement,
    DateElement,
    DropdownElement,
    EnumDropdownElement,
    ExpandedElement,
    FileUploadElement,
    GraphQLSearchElement,
    GridElement,
    GroupElement,
    HeaderElement,
    HiddenElement,
    IElementWithChildren,
    ITenantFormLayout,
    ITenantFormLayoutDefinition,
    MaskedTextFieldElement,
    MultilineTextElement,
    NumberElement,
    PaddingElement,
    PathElement,
    SpacerElement,
    SQLTextElement,
    TenantSearchBoxElement,
    TextFieldElement,
    TimeElement,
    TitleElement,
    VerticalGroupElement,
    RichTextElement,
} from "./OnixElements";
import {LocalTimeField} from "./LocalTimeField";
import {DropdownFromEnum} from "./DropdownFromEnum";
import {DropdownFromList} from "./DropdownFromList";
import {PagedGraphQLPicker} from "./PagedGraphQLPicker";
import {extractDottedLevelsFromObject} from "../helpers/ObjectHelpers";
import "../styles/TenantFormItem.css";
import {SQLEditor} from "./SQLEditor";
import {LoadingButton} from "./LoadingButton";
import {UUIDHelper} from "../helpers/UUIDHelper";
import {FileUploadComponent} from "./FileUploadComponent";
import {QueryData} from "../API";
import {Base64FilePickerComponent} from "./Base64FilePickerComponent";
import {FormAction, TWithStorages, TWithStoragesAndOriginals,} from "../types/TenantTypes";
import {TextFieldWithError} from "./TextFieldWithError";
import {MaskedTextFieldWithError} from "./MaskedTextFieldWithError";
import {CMEditor} from "./CMElement";
import {isElectron} from "../helpers/ElectronHelper";
import {PantheonSessionHelper} from "../helpers/PantheonSessionHelper";
import {PathElementPicker} from "./PathElementPicker";
import {ReactQuillIsolated} from "../helpers/ReactQuillIsolated";

interface ITenantForm<TDatabaseModel, TDto, TReturn> {
    reload: () => void;
    resource: TenantResource;
    addingTitle?: keyof typeof hr;
    editingTitle?: keyof typeof hr;
    form: ITenantFormLayout<TDto>;
    baseDto: TWithStoragesAndOriginals<Partial<TDto>>;
    dismiss?: () => void;
    baseModel: TDatabaseModel;
    stretch?: boolean;
    inline?: boolean;
    children?: (dto?: TDto) => JSX.Element | null;
    storageMapping?: (dto?: TDto) => any;
    getId?: (data: any) => any;
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void;
    additionalIntParameter?: number;
    additionalStringParameter?: string;
    disableWrappingOnChildren?: boolean;
    style?: CSSProperties;
    modifyDtoBeforeSave?: (dto: TDto) => TDto;
    customSubmitButton?: boolean;
    inlineTitle?: (disabled: boolean, submit: () => void) => ReactElement;
    onSubmit?: (dto: TDto) => Promise<void>;
    formDisabled?: boolean;
    minFormWidth?: number | string;
    beforeSubmitForm?: TBeforeSubmitFormCallback<TDto, TReturn>;
    formKeyVisibilityCheck?: (dto: TDto, formKey: string) => boolean;
    dontSubmitOnEnter?: boolean;
    hideSaveAndCloseOnForm?: boolean;
}

// FIXME
// This component is a mess, and it should be refactored

const RichTextField = ({
                           label,
                           title,
                           value,
                           disabled,
                           minHeight,
                           cacheIdentifier,
                           onChange,
                       }: {
    label: string;
    title: string;
    value?: string;
    disabled?: boolean;
    minHeight?: number;
    cacheIdentifier: string;
    onChange: (content: string | null) => void;
}) => {
    const lastEmittedContentRef = useRef(value ?? "");

    useEffect(() => {
        lastEmittedContentRef.current = value ?? "";
    }, [value]);

    const emitContentChange = useCallback(
        (content: string | null) => {
            const nextContent = content ?? "";
            if (lastEmittedContentRef.current === nextContent) {
                return;
            }

            lastEmittedContentRef.current = nextContent;
            onChange(nextContent);
        },
        [onChange],
    );

    return (
        <Stack title={title} className="tenant-form-richtext-element">
            <Label>{label}</Label>
            <ReactQuillIsolated
                cacheIdentifier={cacheIdentifier}
                value={value ?? ""}
                disabled={disabled}
                backgroundColor={"white"}
                minHeight={minHeight ?? 320}
                onContentChange={emitContentChange}
                onBlurContent={emitContentChange}
            />
        </Stack>
    );
};

export const TenantForm = <TDatabaseModel, TDto, TReturn>({
                                                              dismiss,
                                                              inline,
                                                              stretch,
                                                              storageMapping,
                                                              reload,
                                                              children,
                                                              addingTitle,
                                                              baseModel,
                                                              baseDto,
                                                              editingTitle,
                                                              form,
                                                              resource,
                                                              getId,
                                                              afterFormAction,
                                                              additionalIntParameter,
                                                              additionalStringParameter,
                                                              disableWrappingOnChildren,
                                                              style,
                                                              modifyDtoBeforeSave,
                                                              customSubmitButton,
                                                              inlineTitle,
                                                              onSubmit,
                                                              formDisabled,
                                                              minFormWidth,
                                                              beforeSubmitForm,
                                                              formKeyVisibilityCheck,
                                                              dontSubmitOnEnter,
                                                              hideSaveAndCloseOnForm,
                                                          }: ITenantForm<TDatabaseModel, TDto, TReturn>) => {
    const {save, model, setModel, dto, setDto, dtoErrors, loading} =
        useTenantResourceSingle<TDatabaseModel, TDto, TReturn>(
            resource,
            true,
            undefined,
            getId,
            additionalIntParameter,
            additionalStringParameter,
            afterFormAction,
            modifyDtoBeforeSave,
            beforeSubmitForm,
        );
    const {t} = useTranslation();
    const [initialized, setInitialized] = useState(false);
    const [
        loadingFiles,
        {setTrue: setLoadingFiles, setFalse: setNotLoadingFiles},
    ] = useBoolean(false);
    const [loadingLocal, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const uniqueClientId = useMemo(() => UUIDHelper(), []);

    const compareOnlyDto = useCallback((d: TDto, m: TDatabaseModel): boolean => {
        if ((!d && m) || (d && !m)) return false;
        if (!d && !m) return true;
        return Object.keys(d as {}).reduce<boolean>(
            (previousValue, currentValue) => {
                if (currentValue.endsWith("storage")) return previousValue;
                return (
                    previousValue &&
                    _.isEqual((d as any)[currentValue], (m as any)[currentValue])
                );
            },
            true,
        );
    }, []);

    const changed = useMemo(
        () => !_.isEqualWith(dto, model, compareOnlyDto),
        [model, dto],
    );

    const formValid = useMemo(() => {
        if (!dto) return false;
        const validateRecursively = (layout: ITenantFormLayout<TDto>): boolean =>
            Object.values(layout).every((definition: any) => {
                if (!definition?.type) return true;
                if (definition.type instanceof CustomRenderElement) {
                    return definition.type.validate?.(dto) ?? true;
                }
                const childElements = (definition.type as IElementWithChildren<TDto>).elements;
                return childElements ? validateRecursively(childElements) : true;
            });
        return validateRecursively(form);
    }, [dto, form]);

    // useEffect(() => {
    //     if (dto) {
    //         Object.keys(dto).forEach(key => {
    //             if ((dto as any)[key] instanceof Object && !key.endsWith("storage")) {
    //                 console.error("Object found in dto: " + key, "and is not a storage key");
    //             }
    //         });
    //     }
    // }, [dto]);

    useEffect(() => {
        if (model) {
            let tempModel = JSON.parse(JSON.stringify(model));
            for (const k of Object.keys(tempModel)) {
                if (tempModel[k] instanceof Object) {
                    const navigationBaseKey = Object.keys(tempModel).find(
                        (f) => f === k.replace("Navigation", ""),
                    );
                    if (k.endsWith("Navigation") && navigationBaseKey) {
                        tempModel[navigationBaseKey + "storage"] = tempModel[k];
                    } else if (!k.endsWith("storage")) {
                        tempModel[k + "Idstorage"] = tempModel[k];
                    }
                }
            }

            if (storageMapping) tempModel = storageMapping(tempModel);
            setDto(tempModel);
        }
    }, []);

    useEffect(() => {
        if (baseModel) {
            let tempModel = JSON.parse(JSON.stringify(baseModel));
            for (const k of Object.keys(tempModel)) {
                if (tempModel[k] instanceof Object) {
                    const navigationBaseKey = Object.keys(tempModel).find(
                        (f) => f === k.replace("Navigation", ""),
                    );
                    if (k.endsWith("Navigation") && navigationBaseKey) {
                        tempModel[navigationBaseKey + "storage"] = tempModel[k];
                    } else if (!k.endsWith("storage")) {
                        tempModel[k + "Idstorage"] = tempModel[k];
                    }
                }
            }

            if (storageMapping) tempModel = storageMapping(tempModel);
            setModel(tempModel);
        } else setDto(baseDto as any);
    }, [storageMapping]);

    const submit = useCallback(
        (close: boolean) => {
            if (!formValid) {
                return;
            }
            if (onSubmit) {
                onSubmit(dto as any);
                return;
            }
            save({uniqueClientId}).then((response) => {
                console.log("close", close, dismiss);
                if (close && !inline) {
                    if (dismiss) dismiss();
                    reload();
                } else {
                    let tempModel = JSON.parse(JSON.stringify(response));
                    for (const k of Object.keys(tempModel)) {
                        if (tempModel[k] instanceof Object) {
                            const navigationBaseKey = Object.keys(tempModel).find(
                                (f) => f === k.replace("Navigation", ""),
                            );
                            if (k.endsWith("Navigation") && navigationBaseKey) {
                                tempModel[navigationBaseKey + "storage"] = tempModel[k];
                            } else if (!k.endsWith("storage")) {
                                tempModel[k + "Idstorage"] = tempModel[k];
                            }
                        }
                    }

                    if (storageMapping) tempModel = storageMapping(tempModel);
                    setModel(tempModel);
                }
            });
        },
        [
            save,
            uniqueClientId,
            dismiss,
            inline,
            reload,
            storageMapping,
            setModel,
            dto,
            onSubmit,
            formValid,
        ],
    );

    const submitForm = useCallback(
        (e?: FormEvent<HTMLFormElement>) => {
            e?.preventDefault();
            submit(!children);
        },
        [submit, children],
    );

    interface IDependenciesFlattened {
        [key: string]: {
            [key: string]: (
                data: TWithStoragesAndOriginals<TDto>,
                initialized: boolean,
            ) => Promise<{ storage: any; value: any } | any>;
        };
    }

    const flattenDependenciesRecursively = useCallback(
        (form: ITenantFormLayout<TDto>): IDependenciesFlattened => {
            const dependencies: IDependenciesFlattened = {};
            for (const formKey of Object.keys(form)) {
                const formElement = form[
                    formKey as keyof ITenantFormLayout<TDto>
                    ] as ITenantFormLayoutDefinition<TDto>;
                if (formElement.dependsOn) {
                    for (const dependency of formElement.dependsOn) {
                        const key = dependency.key as string;
                        dependencies[key] = dependencies[key] || [];
                        dependencies[key][formKey] = dependency.onChanged;
                    }
                } else if ((formElement.type as IElementWithChildren<TDto>).elements) {
                    const elementWithChildren =
                        formElement.type as IElementWithChildren<TDto>;
                    const childDependencies = flattenDependenciesRecursively(
                        elementWithChildren.elements,
                    );
                    for (const childDependencyKey of Object.keys(childDependencies)) {
                        dependencies[childDependencyKey] =
                            dependencies[childDependencyKey] || {};
                        for (const childDependency of Object.keys(
                            childDependencies[childDependencyKey],
                        )) {
                            dependencies[childDependencyKey][childDependency] =
                                childDependencies[childDependencyKey][childDependency];
                        }
                    }
                }
            }
            return dependencies;
        },
        [form],
    );

    const memoizedDependencies = useMemo(() => {
        const dependencies = flattenDependenciesRecursively(form);
        console.log(dependencies);
        return dependencies;
    }, [flattenDependenciesRecursively]);

    const fieldChanged = useCallback(
        async (formKey: keyof TDto, fieldValue: any) => {
            let onChangedUpdate: any = dto;
            onChangedUpdate = {...onChangedUpdate, ...fieldValue};
            const dependencies = memoizedDependencies[formKey as string];
            const hadDependentFields: boolean = !!dependencies?.length;
            if (dependencies) {
                setLoading();
                try {
                    for (const dependencyKey of Object.keys(dependencies)) {
                        const dependency = dependencies[dependencyKey];
                        const result = await dependency(onChangedUpdate, initialized);
                        if (result) {
                            if (result?.storage || result?.hasOwnProperty("storage")) {
                                onChangedUpdate = {
                                    ...onChangedUpdate,
                                    [dependencyKey]: result.value,
                                    [dependencyKey + "storage"]: result.storage,
                                };
                            } else {
                                onChangedUpdate = {
                                    ...onChangedUpdate,
                                    [dependencyKey]: result,
                                };
                            }
                        }
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setNotLoading();
                }
            }
            if (hadDependentFields) {
                setDto({} as any);
                setImmediate(() => setDto({...onChangedUpdate}));
            } else setDto({...onChangedUpdate});
        },
        [dto, setDto, setLoading, initialized, setNotLoading, memoizedDependencies],
    );

    useEffect(() => {
        if (form && dto && Object.keys(dto).length && !initialized) {
            console.log(
                "ponovna inicijalizacija form, dto ili initialized varijable",
            );
            setInitialized(true);
            (async () => {
                let onChangedUpdate: any = dto;
                onChangedUpdate = {...onChangedUpdate};

                try {
                    for (const dependencyKey of Object.keys(memoizedDependencies)) {
                        const dependencies = memoizedDependencies[dependencyKey];
                        for (const dependency of Object.keys(dependencies)) {
                            const dependencyFunction = dependencies[dependency];
                            await dependencyFunction(onChangedUpdate, initialized);
                        }
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setNotLoading();
                }
            })();
        }
    }, [form, dto, initialized, memoizedDependencies, setNotLoading]);

    /* NOTE
        
        ako ne radi neki customsearchelement, možda se koristi npr: 
        subject,
        umjesto subjectId na formi,
        mora biti subjectId, jer to je ono što se postavlja, a subject se samo koristi za mapiranje na postojeću vrijednost
    
        */

    /*
    
        može se dodati na Stack ispod form za scrollable, izgleda loše na malim formama
    
         style={children && !inline ? {
                    overflowY: "scroll",
                    maxHeight: "65vh",
                } : undefined}
    
        */

    // const renderElementForKey = useCallback(
    //   (
    //     k: String,
    //     form: ITenantFormLayout<TDto>,
    //     alignment?: "HORIZONTAL" | "VERTICAL"
    //   ): ReactElement | null => {
    // return <RenderElementForKeyExported
    //   fieldChanged={fieldChanged}
    //   dto={dto}
    //   t={t}
    //   formDisabled={formDisabled}
    //   setDto={setDto}
    //   loading={loading}
    //   loadingLocal={loadingLocal}
    //   dtoErrors={dtoErrors}
    //   getId={getId}
    //   uniqueClientId={uniqueClientId}
    //   setLoadingFiles={setLoadingFiles}
    //   setNotLoadingFiles={setNotLoadingFiles}
    //   k={k}
    //   form={form}
    //   alignment={alignment}
    // />
    //   },
    //   [
    // dto,
    // loading,
    // loadingLocal,
    // t,
    // dtoErrors,
    // fieldChanged,
    // getId,
    // uniqueClientId,
    // setLoadingFiles,
    // setNotLoadingFiles,
    // setDto,
    // formDisabled,
    //   ]
    // );

    const onFormSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e?.preventDefault();
            if (!dontSubmitOnEnter) {
                submitForm(e);
            } else {
                const form = e.currentTarget as HTMLFormElement;
                // find next input
                const activeElement = document.activeElement;

                if (activeElement) {
                    // Get all focusable elements in the form
                    const focusableElements = Array.from(
                        form.querySelectorAll(
                            'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
                        ),
                    );

                    // Find the index of the currently focused element
                    const currentIndex = focusableElements.indexOf(activeElement);

                    // Move focus to the next focusable element if it exists
                    if (
                        currentIndex >= 0 &&
                        currentIndex < focusableElements.length - 1
                    ) {
                        const nextElement = focusableElements[currentIndex + 1];
                        (nextElement as any).focus();
                    }
                }
            }
        },
        [submitForm, dontSubmitOnEnter],
    );

    const formElement = useMemo(() => {
        if (!dto) return null;
        return (
            <form
                onSubmit={onFormSubmit}
                noValidate
                style={{
                    minWidth: minFormWidth,
                }}
            >
                <Stack
                    tokens={{childrenGap: 10}}
                    wrap
                    className="tenant-form-item-element"
                >
                    {Object.keys(form).map((k) => (
                        <RenderElementForKeyExported
                            fieldChanged={fieldChanged}
                            dto={dto}
                            t={t}
                            formKeyVisibilityCheck={formKeyVisibilityCheck}
                            formDisabled={formDisabled}
                            setDto={setDto}
                            loading={loading}
                            loadingLocal={loadingLocal}
                            dtoErrors={dtoErrors}
                            getId={getId}
                            uniqueClientId={uniqueClientId}
                            setLoadingFiles={setLoadingFiles}
                            setNotLoadingFiles={setNotLoadingFiles}
                            k={k}
                            form={form}
                            // alignment={alignment}
                        />
                    ))}
                    <ShowApiError errors={dtoErrors} keys={[""]}/>
                    <input
                        type="submit"
                        style={{position: "absolute", left: -9999, width: 1, height: 1}}
                    />
                </Stack>
            </form>
        );
    }, [
        dto,
        submitForm,
        form,
        dtoErrors,
        minFormWidth,
        loading,
        loadingLocal,
        t,
        dtoErrors,
        fieldChanged,
        getId,
        uniqueClientId,
        setLoadingFiles,
        setNotLoadingFiles,
        setDto,
        formDisabled,
    ]);

    if (inline)
        return (
            <Stack
                horizontal
                tokens={{childrenGap: 10}}
                wrap
                horizontalAlign={"stretch"}
                style={{...style}}
            >
                <StackItem styles={{root: {flex: 1}}}>
                    <Stack tokens={{childrenGap: 10}}>
                        {inlineTitle
                            ? inlineTitle(!changed || !!loading || !formValid, () => submitForm(undefined))
                            : null}
                        {formElement}
                        {!customSubmitButton ? (
                            <LoadingButton
                                styles={{root: {margin: 0}}}
                                disabled={!changed || !formValid}
                                loading={loading}
                                text={t("save")}
                                tabIndex={1000}
                                onClick={() => submit(!children)}
                            />
                        ) : null}
                    </Stack>
                </StackItem>
                {children ? (
                    <StackItem styles={{root: {flex: 3}}}>{children(dto)}</StackItem>
                ) : null}
                {loading && <ProgressIndicator/>}
            </Stack>
        );

    return (
        <Dialog
            hidden={!dto}
            modalProps={{
                isBlocking: true,
            }}
            isClickableOutsideFocusTrap={false}
            dialogContentProps={{type: DialogType.largeHeader}}
            title={
                getId
                    ? getId(dto)
                    : (dto as any)?.id
                        ? t(editingTitle ?? "")
                        : t(addingTitle ?? "")
            }
            onDismiss={
                loading
                    ? () => {
                    }
                    : () => {
                        reload();
                        if (dismiss) dismiss();
                    }
            }
            minWidth={
                minFormWidth ??
                (children && ((getId && getId(dto)) || (dto as any)?.id != undefined)
                    ? "90vw"
                    : stretch
                        ? "75vw"
                        : "30vw")
            }
            styles={{root: {flex: 1}}}
        >
            <Stack
                horizontal
                tokens={{childrenGap: 10}}
                wrap={!disableWrappingOnChildren}
                horizontalAlign={"stretch"}
            >
                <StackItem styles={{root: {flex: 1}}}>
                    <Stack tokens={{childrenGap: 10}}>
                        {formElement}
                        <Stack
                            className="tenant-form-item-footer-buttons"
                            horizontal
                            horizontalAlign="end"
                            tokens={{childrenGap: 10}}
                        >
                            <LoadingButton
                                disabled={!changed || loadingFiles || !formValid}
                                loading={loading}
                                tabIndex={1000}
                                text={t("save")}
                                onClick={() => submit(false)}
                            />
                            <VerticalDivider/>
                            {!hideSaveAndCloseOnForm ? (
                                <>
                                    <LoadingButton
                                        disabled={!changed || loadingFiles || !formValid}
                                        loading={loading}
                                        tabIndex={1001}
                                        text={t("save-and-close")}
                                        onClick={() => submit(true)}
                                    />
                                    <VerticalDivider/>
                                </>
                            ) : null}
                            <DefaultButton
                                disabled={loading || loadingFiles}
                                tabIndex={1002}
                                text={changed ? t("cancel") : t("close")}
                                onClick={() => {
                                    reload();
                                    if (dismiss) dismiss();
                                }}
                            />
                        </Stack>
                    </Stack>
                </StackItem>
                {children &&
                ((getId && getId(dto)) || (dto as any)?.id != undefined) ? (
                    <StackItem styles={{root: {flex: 3}}}>{children(dto)}</StackItem>
                ) : null}
                {(loading || loadingFiles) && <ProgressIndicator/>}
            </Stack>
        </Dialog>
    );
};

export function RenderElementForKeyExported<TDto, TDatabaseModel>({
                                                                      dto,
                                                                      dtoErrors,
                                                                      fieldChanged,
                                                                      form,
                                                                      formDisabled,
                                                                      getId,
                                                                      k,
                                                                      loading,
                                                                      loadingLocal,
                                                                      setDto,
                                                                      t,
                                                                      uniqueClientId,
                                                                      setLoadingFiles,
                                                                      setNotLoadingFiles,
                                                                      alignment,
                                                                      withoutLabels,
                                                                      formKeyVisibilityCheck,
                                                                  }: {
    fieldChanged: (formKey: keyof TDto, fieldValue: any) => Promise<void>;
    dto:
        | (TDto &
        Partial<TDatabaseModel> &
        TWithStorages<TDto & Partial<TDatabaseModel>>)
        | undefined;
    t: TFunction;
    formDisabled: boolean | undefined;
    setDto: (
        dto: TDto &
            Partial<TDatabaseModel> &
            TWithStorages<TDto & Partial<TDatabaseModel>>,
    ) => void;
    loading: boolean | undefined;
    loadingLocal: boolean | undefined;
    dtoErrors: any;
    getId: ((data: any) => any) | undefined;
    uniqueClientId: string;
    setLoadingFiles: () => void;
    setNotLoadingFiles: () => void;
    withoutLabels?: boolean;
    k: String;
    form: ITenantFormLayout<TDto>;
    alignment?: "HORIZONTAL" | "VERTICAL";
    formKeyVisibilityCheck?: (dto: TDto, formKey: string) => boolean;
}): ReactElement | null {
    if (!dto) {
        console.error("dto nije postavljen a crtali bi!");
        return null;
    }

    let formKey = k as keyof TDto;
    const element = (form as any)[formKey];
    if (!!element.actualField) {
        formKey = element.actualField;
    }
    if (!element?.type) {
        throw new Error(
            `Element na ključu '${k}, nije element! nego ima ključeve: ${Object.keys(
                element,
            )}'`,
        );
    }
    if (!element) {
        console.error(`Element na ključu '${k}' ne postoji u formi!`, form, dto);
        return null;
    }
    if (!!element.hiddenIf && element.hiddenIf(dto)) {
        return null;
    }

    if (formKeyVisibilityCheck) {
        if (!formKeyVisibilityCheck(dto, formKey as string)) {
            return null;
        }
    }

    const translationKey = element.translationKey
        ? element.translationKey
        : (formKey as string);
    const descriptionKey = element.description
        ? element.description
        : `${translationKey}ElementDescription`;
    const disabled =
        formDisabled ||
        loading ||
        loadingLocal ||
        element.disabled ||
        (element.disabledIf && element.disabledIf(dto));
    const showAsErrored = element.showErrorIf && element.showErrorIf(dto);

    const titleValue = t(descriptionKey);
    let labelValue = t(translationKey);
    const placeholdeValue = t(translationKey);

    if (withoutLabels) {
        // titleValue = "";
        labelValue = "";
    }

    // if (!!search && !element.type.elements) {
    //     const searchText = ((formKey as string) +
    //         k +
    //         translationKey +
    //         t(translationKey) +
    //         t(formKey as string)
    //     ).toLowerCase();
    //     const searchTerm = search.toLowerCase();
    //     if (searchText.indexOf(searchTerm) === -1) {
    //         return null;
    //     }
    // }

    if (element.type instanceof HeaderElement) {
        return (
            <Text
                className="tenant-form-header-element"
                key={formKey as string}
                variant={"xLarge"}
                title={titleValue}
            >
                {t(element.type.title ?? translationKey)}
            </Text>
        );
    } else if (element.type instanceof TitleElement) {
        let value: any = dto[formKey];

        if (element.type.titleFormatter) {
            value = element.type.titleFormatter(dto);
        }

        if (element.type.hideIfEmpty && !value) return null;

        return (
            <Stack
                title={titleValue}
                className="tenant-form-title-element"
                key={formKey as string}
                tokens={{childrenGap: 10}}
                horizontalAlign={
                    element.type.horizontal
                        ? "space-between"
                        : element.type.alignRight
                            ? "end"
                            : undefined
                }
                horizontal={element.type.horizontal}
                verticalAlign="start"
                style={{
                    borderBottom: element.type.divided ? "1px solid #33333333" : "",
                }}
            >
                {!element.type.hideLabel ? (
                    <Label style={{padding: 0}}>{t(translationKey)}</Label>
                ) : null}
                <Text>{value}</Text>
            </Stack>
        );
    } else if (element.type instanceof CustomSearchBoxElement) {
        const searchUrl =
            typeof element.type.url == "string"
                ? element.type.url
                : element.type.url(dto);
        const titleKeyIsNested = element.type.titleKey.indexOf(".") > -1;
        const titleKeyIsComposite =
            (element.type.titleKey?.indexOf("+") ?? -1) > -1;
        const subtitleKeyIsNested =
            (element.type.subtitleKey?.indexOf(".") ?? -1) > -1;
        const subtitleKeyIsComposite =
            (element.type.subtitleKey?.indexOf("+") ?? -1) > -1;
        let subtitleCompositeSegments: string[] = [];

        if (subtitleKeyIsComposite) {
            subtitleCompositeSegments = element.type.subtitleKey?.split("+") ?? [];
        }
        let titleCompositeSegments: string[] = [];

        if (titleKeyIsComposite) {
            titleCompositeSegments = element.type.titleKey?.split("+") ?? [];
        }

        let sb: Partial<QueryData> | undefined = {};
        let keepSb = false;

        if (element.type.query) {
            sb.query = element.type.query as any;
            keepSb = true;
        }

        if (element.type.orderBy) {
            sb.orderBy = element.type.orderBy as any;
            keepSb = true;
        }

        if (element.type.orderByDescending != undefined) {
            sb.orderDescending = element.type.orderByDescending;
            keepSb = true;
        }

        if (!keepSb) sb = undefined;

        const selection = (dto as any)[(formKey as any) + "storage"] as any;

        const originalValue = (dto as any)[formKey];

        let realLabel: any = t(translationKey);

        // if (originalValue && !selection && element.type.ifEmptyShowValue) {
        if (!element.type.dontShowValueInLabel && originalValue) {
            // realLabel += ` (${originalValue})`;
            // }
            realLabel = <span>{realLabel} <b className={"tenant-form-original-value"}>({originalValue})</b></span>;
        }

        if (withoutLabels) {
            realLabel = "";
        }

        return (
            <CustomSearchBox
                paging={element.type.paging}
                title={titleValue}
                placeholder={placeholdeValue}
                searchUrl={searchUrl}
                disabled={disabled}
                key={formKey as string}
                label={realLabel}
                headers={element.type.headers}
                tenantResource={element.type.resource}
                customClassName={`tenant-form-customsearchbox-element ${originalValue ? "tenant-form-item-has-value" : ""}`}
                query={
                    element.type.query ??
                    (!!element.type.queryFromDto
                        ? element.type.queryFromDto(dto)
                        : undefined)
                }
                showError={showAsErrored}
                fullWidth={alignment === "VERTICAL"}
                searchBody={sb}
                prefillSubtitleIfEmpty={element.type.prefillSubtitleIfEmpty}
                listItemKey={element.type.key}
                additionalIntParameter={
                    element.type.additionalIntParameter
                        ? element.type.additionalIntParameter(dto)
                        : undefined
                }
                additionalStringParameter={
                    element.type.additionalStringParameter
                        ? element.type.additionalStringParameter(dto)
                        : undefined
                }
                errorMessage={GetApiError(dtoErrors, formKey)}
                titleKey={
                    !titleKeyIsNested && !titleKeyIsComposite
                        ? (element.type.titleKey as any)
                        : undefined
                }
                makeTitle={
                    element.type.makeTitle
                        ? element.type.makeTitle
                        : titleKeyIsNested
                            ? (extractDottedLevelsFromObject(element.type.titleKey) as any)
                            : titleKeyIsComposite
                                ? (k) => titleCompositeSegments.map((m) => k[m]).join(" ")
                                : undefined
                }
                selected={selection}
                showCancelButton={element.type.showCancelButton}
                dontShowValueInLabel={element.type.dontShowValueInLabel}
                subtitleIsImage={element.type.subtitleIsImage}
                makeSubSubtitle={element.type.makeSubSubtitle}
                type={element.type.customSearchBoxType}
                subtitleKey={
                    !subtitleKeyIsNested && !subtitleKeyIsComposite
                        ? (element.type.subtitleKey as any)
                        : undefined
                }
                makeSubtitle={
                    element.type.makeSubtitle
                        ? element.type.makeSubtitle
                        : subtitleKeyIsNested
                            ? (extractDottedLevelsFromObject(
                                element.type.subtitleKey as any,
                            ) as any)
                            : subtitleKeyIsComposite
                                ? (k) => subtitleCompositeSegments.map((m) => k[m]).join(" ")
                                : undefined
                }
                optionSelected={(o) =>
                    fieldChanged(formKey, {
                        [formKey]: o ? o[(element.type as any).key as any] : undefined,
                        [(formKey as any) + "storage"]: o,
                    })
                }
            />
        );
    } else if (element.type instanceof MultilineTextElement) {
        const multilineType = element.type;
        return (
            <MultilineTextComponent
                title={titleValue}
                label={labelValue}
                multiline
                rows={multilineType.rows}
                maxLength={multilineType.maxLength}
                disabled={disabled}
                className="tenant-form-multilinetext-element"
                key={formKey as string}
                autoFocus={false}
                errorMessage={GetApiError(dtoErrors, formKey)}
                placeholder={placeholdeValue}
                value={dto[formKey] as any}
                onChange={(_, v) =>
                    fieldChanged(formKey, {
                        [formKey]: multilineType.maxLength
                            ? v?.slice(0, multilineType.maxLength)
                            : v,
                    })
                }
                showEditModeToggle={multilineType.showEditModeToggle}
            />
        );
    } else if (element.type instanceof RichTextElement) {
        return (
            <RichTextField
                key={formKey as string}
                label={labelValue}
                title={titleValue}
                value={(dto as any)[formKey as any]}
                disabled={disabled}
                minHeight={element.type.minHeight}
                cacheIdentifier={`${formKey as string}-${uniqueClientId}`}
                onChange={(content) =>
                    fieldChanged(formKey, {
                        [formKey]: content ?? "",
                    })
                }
            />
        );
    } else if (element.type instanceof TextFieldElement) {
        return (
            <TextFieldWithError
                title={titleValue}
                label={labelValue}
                value={dto[formKey] as any}
                placeholder={placeholdeValue}
                autoFocus={false}
                disabled={disabled}
                className="tenant-form-textfield-element"
                key={formKey as string}
                errorMessage={GetApiError(dtoErrors, formKey)}
                autoComplete={"off"}
                autoCapitalize={"none"}
                autoCorrect={"none"}
                onChange={(_, v) => fieldChanged(formKey, {[formKey]: v})}
            />
        );
    } else if (element.type instanceof NumberElement) {
        return (
            <TextFieldWithError
                title={titleValue}
                label={labelValue}
                value={dto[formKey] as any}
                placeholder={placeholdeValue}
                autoFocus={false}
                noValidate
                className="tenant-form-number-element"
                key={formKey as string}
                errorMessage={GetApiError(dtoErrors, formKey)}
                disabled={disabled}
                autoComplete={"off"}
                autoCapitalize={"none"}
                autoCorrect={"none"}
                formNoValidate
                type="text"
                onChange={(_, v) => {
                    const normalized = (v ?? "").replace(/,/g, ".").trim();
                    // Avoid sending "" for nullable numeric fields (backend JSON conversion errors).
                    fieldChanged(formKey, {[formKey]: normalized === "" ? null : normalized});
                }}
            />
        );
    } else if (element.type instanceof DateElement) {
        return (
            <LocalDatePicker
                title={titleValue}
                label={translationKey}
                value={dto[formKey] as any}
                disableAutoFocus={dto && getId ? getId(dto) : (dto as any)["id"]}
                disabled={disabled}
                short={element.type.short}
                key={formKey as string}
                className="tenant-form-localdatepicker-element"
                keepCompact={true}
                showCancel={element.type.showCancel}
                utcOnly={element.type.utcOnly}
                maxDate={element.type.maxDate}
                errorMessage={GetApiError(dtoErrors, formKey)}
                onSelectDate={(d) => fieldChanged(formKey, {[formKey]: d})}
            />
        );
    } else if (element.type instanceof FileUploadElement) {
        const id = getId ? getId(dto) : (dto as any)["id"];
        return (
            <>
                <FileUploadComponent
                    title={titleValue}
                    uniqueClientId={uniqueClientId}
                    hideTopBar={!element.type.showTopBar}
                    className="tenant-form-fileupload-element"
                    key={formKey as string}
                    baseDto={
                        id != undefined && id != null
                            ? element.type.baseDto(dto)
                            : {uniqueClientId}
                    }
                    query={
                        id != undefined && id != null
                            ? element.type.query(dto)
                            : {uniqueClientId: {Equals: uniqueClientId}}
                    }
                    selectionChanged={element.type.selectionChanged}
                    disabled={disabled}
                    loadingCallback={setLoadingFiles}
                    loadedCallback={setNotLoadingFiles}
                    nameOverride={(element.type as FileUploadElement<TDto>).nameOverride}
                    prependOriginalFileName={
                        (element.type as FileUploadElement<TDto>).prependOriginalFileName
                    }
                    keepOriginalExtension={
                        (element.type as FileUploadElement<TDto>).keepOriginalExtension
                    }
                    maxXorY={(element.type as any).maxXorY}
                    translationKey={translationKey as any}
                    additionalDirectorySegments={
                        (element.type as FileUploadElement<TDto>)
                            .additionalDirectorySegments
                    }
                    showDocumentList
                    showUserAuthorization={element.type.showUserAuthorization}
                />
                {element.addon ? element.addon(dto) : null}
            </>
        );
    } else if (element.type instanceof CheckboxElement) {
        if (element.type.useString) {
            return (
                <Stack
                    title={titleValue}
                    key={formKey as string}
                    className="tenant-form-stringcheckbox-element"
                >
                    <Checkbox
                        label={labelValue}
                        checked={(dto[formKey] as any) == "T"}
                        disabled={disabled ?? element.type.disabled}
                        indeterminate={element.type.tristate && ((dto[formKey] as any) == null || (dto[formKey] as any) == undefined)}
                        onChange={(_, checked) => {
                            // fieldChanged(formKey, {[formKey]: checked ? "T" : "F"});
                            if(element.type.tristate) {
                                if((dto[formKey] as any) == "T") {
                                    fieldChanged(formKey, {[formKey]: "F"});
                                }else if((dto[formKey] as any) == "F") {
                                    fieldChanged(formKey, {[formKey]: null});
                                }else {
                                    fieldChanged(formKey, {[formKey]: "T"});
                                }
                            }else {
                                fieldChanged(formKey, {[formKey]: checked ? "T" : "F"});
                            }
                        }}
                    />
                    <ShowApiError errors={dtoErrors} keys={[formKey]}/>
                </Stack>
            );
        }
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-checkbox-element"
            >
                <Checkbox
                    title={titleValue}
                    label={labelValue}
                    checked={!!dto[formKey]}
                    disabled={disabled ?? element.type.disabled}
                    indeterminate={element.type.tristate}
                    onChange={(_, checked) =>
                        fieldChanged(formKey, {[formKey]: checked})
                    }
                />
                <ShowApiError errors={dtoErrors} keys={[formKey]}/>
            </Stack>
        );
    } else if (element.type instanceof DateAndTimeElement) {
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-dateandtime-element"
            >
                <LocalDatePicker
                    title={titleValue}
                    label={translationKey}
                    value={dto[formKey] as any}
                    disableAutoFocus={dto && getId ? getId(dto) : (dto as any)["id"]}
                    disabled={disabled}
                    errorMessage={GetApiError(dtoErrors, formKey)}
                    onSelectDate={(d) => fieldChanged(formKey, {[formKey]: d})}
                />
                <LocalTimeField
                    title={titleValue}
                    label={t("time")}
                    onValidChange={(v) => fieldChanged(formKey, {[formKey]: v})}
                    disabled={disabled}
                    date={dto[formKey] as any}
                />
            </Stack>
        );
    } else if (element.type instanceof TimeElement) {
        return (
            <MaskedTextFieldWithError
                title={titleValue}
                label={t(translationKey) + " (HH:MM / HH.MM)"}
                mask={"99:99"}
                className="tenant-form-time-element"
                disabled={disabled}
                key={formKey as string}
                errorMessage={GetApiError(dtoErrors, formKey)}
                value={(dto[formKey] as any) ?? ""}
                onChange={(_, v) => fieldChanged(formKey, {[formKey]: v})}
            />
        );
    } else if (element.type instanceof MaskedTextFieldElement) {
        return (
            <MaskedTextFieldWithError
                title={titleValue}
                label={labelValue}
                mask={element.type.mask}
                className="tenant-form-masked-text-element"
                disabled={disabled}
                key={formKey as string}
                errorMessage={GetApiError(dtoErrors, formKey)}
                value={(dto[formKey] as any) ?? ""}
                onChange={(_, v) => fieldChanged(formKey, {[formKey]: v})}
            />
        );
    } else if (element.type instanceof EnumDropdownElement) {
        return (
            <DropdownFromEnum
                title={titleValue}
                value={dto[formKey] as any}
                className="tenant-form-enumdropdown-element"
                key={formKey as string}
                disabled={disabled}
                iconFromValue={element.type.iconFromValue}
                rightSideIconButtonProps={element.type.rightSideIconButtonProps}
                showCancel={element.type.showCancel}
                multiple={element.type.multiple}
                errorMessage={GetApiError(dtoErrors, formKey)}
                label={labelValue}
                object={element.type.enumData}
                setValue={(v) => fieldChanged(formKey, {[formKey]: v})}
            />
        );
    } else if (element.type instanceof DropdownElement) {
        return (
            <DropdownFromList
                title={titleValue}
                className="tenant-form-dropdown-element"
                key={formKey as string}
                toKND={(t1) => ({
                    data: t1,
                    key: (t1 as any)[(element.type as any).key as any],
                    name: (t1 as any)[(element.type as any).titleKey as any],
                })}
                list={element.type.values}
                showEmpty={element.type.showEmpty}
                setValue={(o) =>
                    fieldChanged(formKey, {
                        [formKey]: o
                            ? (o as any)[(element.type as any).key as any]
                            : undefined,
                        [(formKey as any) + "storage"]: o,
                    })
                }
                label={labelValue}
                disabled={element.disabled}
                errorMessage={GetApiError(dtoErrors, formKey)}
                value={(dto as any)[(formKey as any) + "storage"] as any}
            />
        );
    } else if (element.type instanceof PaddingElement) {
        return (
            <div
                title={titleValue}
                className="tenant-form-padding-element"
                key={"padding-element-" + slugify(formKey.toString())}
                style={{height: element.type.amount}}
            >
                &nbsp;
            </div>
        );
    } else if (element.type instanceof GraphQLSearchElement) {
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-graphqlsearch-element"
            >
                <Label>{t(translationKey)}</Label>
                <PagedGraphQLPicker
                    title={titleValue}
                    key={formKey as string}
                    query={element.type.query}
                    queryKey={element.type.queryKey}
                    disabled={disabled}
                    queryResultKey={element.type.queryResultKey}
                    queryResultName={element.type.queryResultName}
                    errorMessage={GetApiError(dtoErrors, formKey)}
                    value={(dto as any)[(formKey as any) + "storage"] as any}
                    setValue={(o) =>
                        fieldChanged(formKey, {
                            [formKey]: o
                                ? o[(element.type as any).queryResultKey]
                                : undefined,
                            [(formKey as any) + "storage"]: o,
                        })
                    }
                />
            </Stack>
        );
    } else if (element.type instanceof HiddenElement) {
        return null; //<span key={formKey as string}>&nbsp;</span>
    } else if (element.type instanceof TenantSearchBoxElement) {
        const titleKeyIsNested = element.type.titleKey.indexOf(".") > -1;
        const titleKeyIsComposite =
            (element.type.titleKey?.indexOf("+") ?? -1) > -1;
        const subtitleKeyIsNested =
            (element.type.subtitleKey?.indexOf(".") ?? -1) > -1;
        const subtitleKeyIsComposite =
            (element.type.subtitleKey?.indexOf("+") ?? -1) > -1;
        let subtitleCompositeSegments: string[] = [];

        if (subtitleKeyIsComposite) {
            subtitleCompositeSegments = element.type.subtitleKey?.split("+") ?? [];
        }
        let titleCompositeSegments: string[] = [];

        if (titleKeyIsComposite) {
            titleCompositeSegments = element.type.titleKey?.split("+") ?? [];
        }

        return (
            <CustomSearchBox
                paging={element.type.paging}
                title={titleValue}
                placeholder={placeholdeValue}
                customClassName="tenant-form-tenantsearchbox-element"
                searchUrl={element.type.resource + "/List"}
                disabled={disabled}
                key={formKey as string}
                additionalIntParameter={
                    element.type.additionalIntParameter
                        ? element.type.additionalIntParameter(dto)
                        : undefined
                }
                additionalStringParameter={
                    element.type.additionalStringParameter
                        ? element.type.additionalStringParameter(dto)
                        : undefined
                }
                label={labelValue}
                headers={element.type.headers}
                tenantResource={element.type.resource}
                listItemKey={element.type.key}
                errorMessage={GetApiError(dtoErrors, formKey)}
                titleKey={
                    !titleKeyIsNested && !titleKeyIsComposite
                        ? (element.type.titleKey as any)
                        : undefined
                }
                makeTitle={
                    titleKeyIsNested
                        ? (extractDottedLevelsFromObject(element.type.titleKey) as any)
                        : titleKeyIsComposite
                            ? (k) => titleCompositeSegments.map((m) => k[m]).join(" ")
                            : undefined
                }
                selected={(dto as any)[(formKey as any) + "storage"] as any}
                type={"DEFAULT_CRUD"}
                subtitleKey={
                    !subtitleKeyIsNested && !subtitleKeyIsComposite
                        ? (element.type.subtitleKey as any)
                        : undefined
                }
                makeSubtitle={
                    subtitleKeyIsNested
                        ? (extractDottedLevelsFromObject(
                            element.type.subtitleKey as any,
                        ) as any)
                        : subtitleKeyIsComposite
                            ? (k) => subtitleCompositeSegments.map((m) => k[m]).join(" ")
                            : undefined
                }
                optionSelected={(o) =>
                    fieldChanged(formKey, {
                        [formKey]: o ? o[(element.type as any).key as any] : undefined,
                        [(formKey as any) + "storage"]: o,
                    })
                }
            />
        );
    } else if (element.type instanceof SQLTextElement) {
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-sqltext-element"
            >
                <Label>{t(translationKey)}</Label>
                <SQLEditor
                    disabled={disabled}
                    sql={(dto as any)[formKey as any] as any}
                    sqlChanged={(v) =>
                        fieldChanged(formKey, {
                            [formKey]: v,
                        })
                    }
                />
            </Stack>
        );
    } else if (element.type instanceof CustomRenderElement) {
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-customrender-element"
            >
                <Label>{t(translationKey)}</Label>
                {element.type.renderer(dto, setDto as any)}
            </Stack>
        );
    } else if (element.type instanceof ButtonElement) {
        const localDisabled = element.type.disabledIf?.(dto) ?? disabled;
        return (
            <PrimaryButton
                disabled={localDisabled}
                title={titleValue}
                key={formKey as string}
                onClick={() => {
                    if (element.type?.onClick) {
                        element.type?.onClick(dto, setDto);
                    }
                }}
                text={element.type.text}
                className="tenant-form-button-element"
                style={{
                    backgroundColor: element.type.color,
                }}
            />
        );
    } else if (element.type instanceof BlobElement) {
        const blobContent = (dto as any)[formKey as any];

        return (
            <Stack
                key={formKey as string}
                className="tenant-form-blob-element"
                title={titleValue}
            >
                <Label>{t(translationKey)}</Label>
                <Stack horizontal title={titleValue}>
                    <Base64FilePickerComponent
                        title={titleValue}
                        disabled={disabled}
                        base64Content={blobContent}
                        base64FilePicked={(b) => {
                            return setDto({
                                ...dto,
                                [formKey]: b,
                            });
                        }}
                    />
                </Stack>
            </Stack>
        );
    } else if (element.type instanceof GroupElement) {
        const groupKeys = Object.keys(element.type.elements);

        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                horizontal
                tokens={{childrenGap: 10}}
                className="tenant-form-group-element"
                style={{height: "100%"}}
            >
                {groupKeys.map((key) => (
                    <StackItem style={{flex: 1}} grow={1} key={key} title={titleValue}>
                        <RenderElementForKeyExported<TDto, TDatabaseModel>
                            fieldChanged={fieldChanged}
                            dto={dto}
                            t={t}
                            formKeyVisibilityCheck={formKeyVisibilityCheck}
                            formDisabled={formDisabled || disabled}
                            setDto={setDto}
                            loading={loadingLocal}
                            loadingLocal={loadingLocal}
                            dtoErrors={dtoErrors}
                            getId={getId}
                            uniqueClientId={uniqueClientId}
                            setLoadingFiles={setLoadingFiles}
                            setNotLoadingFiles={setNotLoadingFiles}
                            k={key}
                            form={element.type.elements}
                            alignment={"HORIZONTAL"}
                        />
                    </StackItem>
                ))}
            </Stack>
        );
    } else if (element.type instanceof VerticalGroupElement) {
        const groupKeys = Object.keys(element.type.elements);

        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                grow={1}
                verticalAlign={"start"}
                tokens={{...(element.type.gapless ? {} : {childrenGap: 10})}}
                // verticalFill
                className="tenant-form-verticalgroup-element"
            >
                {groupKeys.map((key) => (
                    <StackItem key={key} style={{flex: 1}} grow={1} title={titleValue}>
                        <RenderElementForKeyExported<TDto, TDatabaseModel>
                            fieldChanged={fieldChanged}
                            dto={dto}
                            t={t}
                            formDisabled={formDisabled || disabled}
                            setDto={setDto}
                            loading={loadingLocal}
                            formKeyVisibilityCheck={formKeyVisibilityCheck}
                            loadingLocal={loadingLocal}
                            dtoErrors={dtoErrors}
                            getId={getId}
                            uniqueClientId={uniqueClientId}
                            setLoadingFiles={setLoadingFiles}
                            setNotLoadingFiles={setNotLoadingFiles}
                            k={key}
                            form={element.type.elements}
                            alignment="VERTICAL"
                        />
                    </StackItem>
                ))}
            </Stack>
        );
    } else if (element.type instanceof GridElement) {
        const elementCount = element.type.elements.length;
        const cols = element.type.columnCount;
        const rows = elementCount / cols;

        if (elementCount % cols !== 0)
            throw `Grid nema broj elemenata dijeljiv s brojem stupaca!`;

        console.log("rows", rows, "cols", cols);

        return (
            <div className="ms-Grid">
                {[...Array(rows)].map((_, i) => (
                    <div key={`${element.type}-${i}`} className="ms-Grid-row">
                        {[...Array(cols)].map((_, j) => (
                            <div
                                className="ms-Grid-col ms-sm6 ms-md4 ms-lg2"
                                key={`${element.type}-${i}-${j}`}
                            >
                                <span>hello</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    } else if (element.type instanceof ExpandedElement) {
        switch (element.type.direction) {
            case "BOTH":
                return <div style={{width: "100%", height: "100%"}}/>;
            case "HORIZONTAL":
                return <div style={{width: "100%"}}/>;
            case "VERTICAL":
                return <div style={{height: "100%"}}/>;
        }
    } else if (element.type instanceof SpacerElement) {
        const endStyle: CSSProperties = {};

        endStyle.height = element.type.height;
        endStyle.width = element.type.width;

        if (element.type.height && element.type.width) {
        } else if (element.type.height) {
            endStyle.width = "100%";
        } else if (element.type.width) {
            endStyle.height = "100%";
        } else {
        }

        return <div className={"spacer"} style={endStyle}/>;
    } else if (element.type instanceof CMEditorElement) {
        return (
            <Stack
                title={titleValue}
                key={formKey as string}
                className="tenant-form-cmeditor-element"
            >
                <Label>{t(translationKey)}</Label>
                <CMEditor
                    tsDefinitions={
                        element.type.tsDefinitions ??
                        (!!element.type.tsDefinitionsFromDto
                            ? element.type.tsDefinitionsFromDto(dto)
                            : undefined)
                    }
                    title={titleValue}
                    minHeight={element.type.minHeight ?? 200}
                    language={element.type.codeLanguage}
                    disabled={disabled}
                    endData={(dto as any)[formKey as any]}
                    saveFile={async (v) => {
                        fieldChanged(formKey, {
                            [formKey]: v,
                        });
                    }}
                />
                <ShowApiError errors={dtoErrors} keys={[formKey]}/>
            </Stack>
        );
    } else if (element.type instanceof PathElement) {
        if (isElectron({}) || PantheonSessionHelper.hasOnixDesktopBridge()) {
            return (
                <PathElementPicker
                    key={formKey as string}
                    title={t(translationKey)}
                    disabled={disabled}
                    path={(dto as any)[formKey as any]}
                    pathPicked={(p) => {
                        fieldChanged(formKey, {
                            [formKey]: p,
                        });
                    }}
                />
            );
        } else {
            return <Label>Path element radi samo na OnixDesktop aplikaciji!</Label>;
        }
    }

    console.log("Not implemented!", element);

    return <span>Not implemented: {element?.type?.type}</span>;
}
